import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  AppBar,
  useTheme,
  IconButton,
  TextField,
  Tooltip,
  CircularProgress,
  Checkbox,
  Menu,
  MenuItem,
  Button,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArchiveIcon from '@mui/icons-material/Archive';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { Note as NoteIcon } from '@mui/icons-material';
import { Sms as SmsIcon } from '@mui/icons-material';
import { getNewHires, updateNewHire, NewHire } from '../../services/hireService';
import Archived from '../archive/Archived';
import { profileService } from '../../services/profileService';
import { progressStageService, ProgressStage } from '../../services/progressStageService';
import TrademarkFooter from '../common/TrademarkFooter';
import { supabase } from '../../config/supabase';
import ConfirmArchive from '../archive/ConfirmArchive';
import MoveHireDialog from '../hire/MoveHireDialog';
import { ownerService } from '../../services/ownerService';
import NotesDialog from '../hire/NotesDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`manager-tabpanel-${index}`}
      aria-labelledby={`manager-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CRMDashboard: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const theme = useTheme();
  const [hires, setHires] = useState<NewHire[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<NewHire>>({});
  const [userFullName, setUserFullName] = useState('');
  const [userRole, setUserRole] = useState<string>('');
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [hireToArchive, setHireToArchive] = useState<NewHire | null>(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedHireForActions, setSelectedHireForActions] = useState<NewHire | null>(null);
  const [moveHireOpen, setMoveHireOpen] = useState(false);
  const [selectedHireForMove, setSelectedHireForMove] = useState<NewHire | null>(null);
  const [hireRatio, setHireRatio] = useState<{ enrolled: number; waiting: number; ratio: number | null } | null>(null);
  const [hireRatioLoading, setHireRatioLoading] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedHireForNotes, setSelectedHireForNotes] = useState<NewHire | null>(null);

  useEffect(() => {
    fetchHires();
    const fetchUserProfile = async () => {
      const profile = await profileService.getCurrentUserProfile();
      if (profile) {
        setUserFullName(profile.full_name);
        setUserRole(profile.role);
        // Fetch hire ratio if user is admin
        if (profile.role === 'admin') {
          fetchHireRatio();
        }
      }
    };
    fetchUserProfile();
  }, []);

  const fetchHireRatio = async () => {
    try {
      console.log('[CRMDashboard] fetchHireRatio called');
      setHireRatioLoading(true);
      const ratio = await ownerService.getCurrentUserHireRatio();
      console.log('[CRMDashboard] Received hire ratio:', ratio);
      setHireRatio(ratio);
    } catch (error) {
      console.error('[CRMDashboard] Error fetching hire ratio:', error);
    } finally {
      setHireRatioLoading(false);
    }
  };

  const fetchHires = async () => {
    try {
      setLoading(true);
      console.log('Fetching hires in CRMDashboard...');
      
      // Fetch progress stages for current user's organization
      // Use DB override so admin sees the same custom labels configured in Progress Stage Settings
      const stages = await progressStageService.getCurrentUserProgressStagesWithDbOverride();
      setProgressStages(stages);
      
      const data = await getNewHires();
      console.log('Received hires in CRMDashboard:', data);
      setHires(data);
    } catch (error) {
      console.error('Error fetching hires:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleEdit = (hire: NewHire) => {
    setEditingId(hire.id);
    setEditFormData(hire);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditFormData({});
  };

  const handleSave = async () => {
    if (editingId) {
      try {
        await updateNewHire(editingId, editFormData);
        await fetchHires(); // Refresh the list after updating
        setEditingId(null);
        setEditFormData({});
      } catch (error) {
        console.error('Error updating hire:', error);
      }
    }
  };

  const handleEditChange = (field: keyof NewHire) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditFormData({
      ...editFormData,
      [field]: event.target.value,
    });
  };

  const handleArchive = async (hire: NewHire) => {
    try {
      // Insert into new_hire_archive table with current timestamp
      const { error: insertError } = await supabase
        .from('new_hire_archive')
        .insert([{
          ...hire,
          created_at: new Date().toISOString() // Set archive timestamp
        }]);

      if (insertError) throw insertError;

      // Remove from new_hire table
      const { error: deleteError } = await supabase
        .from('new_hire')
        .delete()
        .eq('id', hire.id);

      if (deleteError) throw deleteError;

      // Remove from profiles table (new_hire.id = profiles.id)
      const { error: profileDeleteError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', hire.id);

      if (profileDeleteError) {
        console.warn('Error deleting profile (may not exist):', profileDeleteError);
        // Don't throw error - profile might not exist, continue with archive process
      }

      // Refresh the list after archiving
      await fetchHires();
      console.log('Successfully archived hire:', hire.fullName);
      
      // Close the confirmation dialog
      setConfirmArchiveOpen(false);
      setHireToArchive(null);
    } catch (error) {
      console.error('Error archiving hire:', error);
    }
  };

  const handleArchiveClick = (hire: NewHire) => {
    setHireToArchive(hire);
    setConfirmArchiveOpen(true);
  };

  const handleArchiveCancel = () => {
    setConfirmArchiveOpen(false);
    setHireToArchive(null);
  };

  const handleArchiveConfirm = () => {
    if (hireToArchive) {
      handleArchive(hireToArchive);
    }
  };

  // Handle agent appointment sent checkbox change
  const handleAgentAppointmentSentChange = async (hire: NewHire, checked: boolean) => {
    try {
      await updateNewHire(hire.id, { agent_appointment_sent: checked });
      await fetchHires(); // Refresh the list after updating
    } catch (error) {
      console.error('Error updating agent appointment sent status:', error);
    }
  };

  // Actions menu handlers
  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>, hire: NewHire) => {
    setActionsMenuAnchor(event.currentTarget);
    setSelectedHireForActions(hire);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
    setSelectedHireForActions(null);
  };

  const handleEditFromMenu = () => {
    if (selectedHireForActions) {
      handleEdit(selectedHireForActions);
    }
    handleActionsMenuClose();
  };

  const handleArchiveFromMenu = () => {
    if (selectedHireForActions) {
      handleArchiveClick(selectedHireForActions);
    }
    handleActionsMenuClose();
  };

  const handleMoveFromMenu = () => {
    if (selectedHireForActions) {
      setSelectedHireForMove(selectedHireForActions);
      setMoveHireOpen(true);
    }
    handleActionsMenuClose();
  };

  // Handle move success
  const handleMoveSuccess = (updatedHire: NewHire) => {
    // Update the local state with the moved hire
    setHires(hires.map(hire => hire.id === updatedHire.id ? updatedHire : hire));
    console.log('Hire moved successfully:', updatedHire.fullName);
  };

  const tableColumns = [
    'Actions',
    'Notes',
    'Created At',
    'Updated At',
    'Full Name',
    'State',
    'Email',
    'Mobile Number',
    'Instagram',
    'Reference',
    'Recruiter Numbers',
    'Edit',
  ];

  const [progressStages, setProgressStages] = useState<ProgressStage[]>([]);

  const agentNumberCheckboxStage = useMemo(() => {
    if (!progressStages || progressStages.length === 0) return null;

    const pendingStage = progressStages.find(stage =>
      stage.label.toLowerCase().includes('pending waiting for training')
    );
    if (pendingStage) return pendingStage.value;

    const waitingApprovalStage = progressStages.find(stage =>
      stage.label.toLowerCase().includes('waiting agent number approval')
    );
    if (waitingApprovalStage) return waitingApprovalStage.value;

    const genericAgent = progressStages.find(stage =>
      stage.label.toLowerCase().includes('agent number')
    );
    return genericAgent?.value ?? null;
  }, [progressStages]);

  const getTableColumnsForStage = (stageValue: number, agentStageValue: number | null) => {
    if (agentStageValue !== null && stageValue === agentStageValue) {
      const baseColumns = [...tableColumns];
      baseColumns.splice(1, 0, 'Agent Number Sent');
      return baseColumns;
    }
    return tableColumns;
  };

  const hrManagers = ['Active Hires', 'Archived'];

  const renderEditableCell = (hire: NewHire, field: keyof NewHire) => {
    const isEditing = editingId === hire.id;
    const value = isEditing ? editFormData[field] || '' : hire[field];

    if (isEditing && field !== 'created_at' && field !== 'update_timestamp') {
      return (
        <TextField
          fullWidth
          size="small"
          value={value}
          onChange={handleEditChange(field)}
          type="text"
        />
      );
    }

    // Format the timestamp for created_at and update_timestamp fields
    if ((field === 'created_at' || field === 'update_timestamp') && value) {
      const date = new Date(value as string);
      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }

    return value;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper elevation={3}>
        <AppBar 
          position="static" 
          color="default" 
          sx={{ 
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
            borderBottom: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.5)'
              : '0 4px 20px rgba(102, 126, 234, 0.3)',
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                color: theme.palette.mode === 'dark' ? '#ffffff' : '#ffffff',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                '&.Mui-selected': {
                  color: '#ffffff',
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px 8px 0 0',
                },
                '&:hover': {
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px 8px 0 0',
                },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #45b7d1)',
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            {hrManagers.map((manager, index) => (
              <Tab 
                key={index} 
                label={manager}
                sx={{
                  minHeight: 48,
                  fontWeight: selectedTab === index ? 'bold' : 'normal',
                }}
              />
            ))}
          </Tabs>
        </AppBar>

        {hrManagers.map((manager, managerIndex) => (
          <TabPanel key={managerIndex} value={selectedTab} index={managerIndex}>
            {manager === 'Archived' ? (
              <Archived onUnarchiveComplete={fetchHires} />
            ) : (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  mb: 3,
                  p: 2,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.2)'}`,
                }}>
                  <Typography 
                    variant="h5" 
                    sx={{
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, #ffffff 0%, #b3b3b3 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      fontWeight: 700,
                    }}
                  >
                    {userFullName}'s Dashboard
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Refresh Data">
                      <IconButton 
                        onClick={fetchHires} 
                        color="primary"
                        disabled={loading}
                        sx={{ 
                          background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #3a3a3a 0%, #4a4a4a 100%)'
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: '#ffffff',
                          '&:hover': {
                            background: theme.palette.mode === 'dark'
                              ? 'linear-gradient(135deg, #4a4a4a 0%, #5a5a5a 100%)'
                              : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                            transform: 'scale(1.05)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Admin Cards Row: Hire Per Code Ratio + SMS Manager */}
                {userRole === 'admin' && (
                  <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                    <Card
                      elevation={3}
                      sx={{
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)'
                          : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 8px 25px rgba(0, 0, 0, 0.3)'
                            : '0 8px 25px rgba(102, 126, 234, 0.2)',
                        },
                        flex: '1 1 300px',
                        maxWidth: 400,
                      }}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography color="textSecondary" gutterBottom variant="h6">
                              Hire per Code Ratio
                            </Typography>
                            {hireRatioLoading ? (
                              <Box display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" color="textSecondary">
                                  Loading...
                                </Typography>
                              </Box>
                            ) : (
                              <Tooltip title={`Hires (Progress 2-6): ${hireRatio?.enrolled || 0} • Coded (Progress 6): ${hireRatio?.waiting || 0}`}>
                                <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                                  {hireRatio?.ratio === null || hireRatio?.ratio === undefined ? 'N/A' : `${(hireRatio.ratio * 100).toFixed(1)}%`}
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                          <Avatar
                            sx={{
                              bgcolor: theme.palette.info.main,
                              width: 56,
                              height: 56,
                            }}
                          >
                            <TrendingUpIcon />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </Card>

                    <Card
                      elevation={3}
                      onClick={() => { window.location.href = '/sms-manager'; }}
                      sx={{
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)'
                          : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 8px 25px rgba(0, 0, 0, 0.3)'
                            : '0 8px 25px rgba(102, 126, 234, 0.2)',
                        },
                        flex: '1 1 300px',
                        maxWidth: 400,
                      }}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography color="textSecondary" gutterBottom variant="h6">
                              SMS Manager
                            </Typography>
                            <Typography variant="body1" color="textSecondary">
                              Manage leads & messages
                            </Typography>
                          </Box>
                          <Avatar
                            sx={{
                              bgcolor: theme.palette.mode === 'dark' ? '#667eea' : '#764ba2',
                              width: 56,
                              height: 56,
                            }}
                          >
                            <SmsIcon />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                )}
                
                {progressStages.map((stage, stageIndex) => {
                  const stageHires = hires.filter(hire => hire.progress === stage.value);
                  // Get columns for this specific stage
                  let stageTableColumns = getTableColumnsForStage(stage.value, agentNumberCheckboxStage);
                  // For "Invitation Sent" stage (stage.value === 0), exclude "Updated At" column
                  if (stage.value === 0) {
                    stageTableColumns = stageTableColumns.filter(col => col !== 'Updated At');
                  }
                  
                  return (
                    <Box key={stageIndex} sx={{ mb: 4 }}>
                      <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                          mt: 2,
                          p: 2,
                          background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.12) 100%)'
                            : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.3)'}`,
                          fontWeight: 600,
                          color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                        }}
                      >
                        {stage.label} ({stageHires.length})
                      </Typography>
                      <TableContainer 
                        component={Paper} 
                        sx={{ 
                          mb: 2,
                          borderRadius: 3,
                          overflow: 'hidden',
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                            : '0 8px 32px rgba(102, 126, 234, 0.15)',
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.2)'}`,
                          // Mobile-friendly horizontal scrolling
                          overflowX: 'auto',
                          '&::-webkit-scrollbar': {
                            height: 8,
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                            borderRadius: 4,
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                            borderRadius: 4,
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                            },
                          },
                          // Touch scrolling for mobile
                          WebkitOverflowScrolling: 'touch',
                          scrollBehavior: 'smooth',
                        }}
                      >
                        <Table sx={{ 
                          minWidth: { xs: stage.value === agentNumberCheckboxStage ? 950 : 800, md: 'auto' }, // Ensure minimum width on mobile, extra space for checkbox column
                        }}>
                          <TableHead>
                            <TableRow>
                              {stageTableColumns.map((column, index) => (
                                <TableCell 
                                  key={index}
                                  sx={{
                                    background: theme.palette.mode === 'dark' 
                                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.12) 100%)'
                                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                                    color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                    textAlign: 'center',
                                    py: 2,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                                    // Prevent header text wrapping
                                    whiteSpace: 'nowrap',
                                    minWidth: column === 'Agent Number Sent' ? 150 : index === 0 || index === 1 ? 120 : index === 2 ? 150 : 100, // Set minimum widths for key columns
                                  }}
                                >
                                  {column}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {stageHires.map((hire, hireIndex) => (
                              <TableRow 
                                key={hire.id}
                                sx={{
                                  '&:nth-of-type(odd)': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.02)'
                                      : 'rgba(102, 126, 234, 0.03)',
                                  },
                                  '&:nth-of-type(even)': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.05)'
                                      : 'rgba(118, 75, 162, 0.03)',
                                  },
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.08)'
                                      : 'rgba(102, 126, 234, 0.08)',
                                    transform: 'scale(1.01)',
                                    transition: 'all 0.3s ease',
                                  },
                                  transition: 'all 0.3s ease',
                                }}
                              >
                                {/* Actions Column */}
                                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 120, textAlign: 'center' }}>
                                  {editingId === hire.id ? (
                                    <>
                                      <Tooltip title="Save">
                                        <IconButton 
                                          onClick={handleSave} 
                                          sx={{
                                            color: '#4caf50',
                                            '&:hover': {
                                              backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                              transform: 'scale(1.1)',
                                            },
                                            transition: 'all 0.3s ease',
                                          }}
                                        >
                                          <SaveIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Cancel">
                                        <IconButton 
                                          onClick={handleCancel} 
                                          sx={{
                                            color: '#f44336',
                                            '&:hover': {
                                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                              transform: 'scale(1.1)',
                                            },
                                            transition: 'all 0.3s ease',
                                          }}
                                        >
                                          <CancelIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    <Tooltip title="Actions">
                                      <IconButton 
                                        onClick={(event) => handleActionsMenuOpen(event, hire)} 
                                        sx={{
                                          color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
                                          '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark' 
                                              ? 'rgba(144, 202, 249, 0.1)'
                                              : 'rgba(25, 118, 210, 0.1)',
                                            transform: 'scale(1.1)',
                                          },
                                          transition: 'all 0.3s ease',
                                        }}
                                      >
                                        <MoreVertIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                                {/* Notes Column */}
                                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100, textAlign: 'center' }}>
                                  <Tooltip title="Notes">
                                    <IconButton 
                                      onClick={() => {
                                        setSelectedHireForNotes(hire);
                                        setNotesDialogOpen(true);
                                      }}
                                      sx={{
                                        color: theme.palette.mode === 'dark' ? '#667eea' : '#667eea',
                                        '&:hover': {
                                          backgroundColor: theme.palette.mode === 'dark' 
                                            ? 'rgba(102, 126, 234, 0.1)'
                                            : 'rgba(102, 126, 234, 0.1)',
                                          transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.3s ease',
                                      }}
                                    >
                                      <NoteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                                {/* Agent Number Sent Checkbox - configurable per organization */}
                                {agentNumberCheckboxStage !== null && stage.value === agentNumberCheckboxStage && (
                                  <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150, textAlign: 'center' }}>
                                    <Checkbox
                                      checked={hire.agent_appointment_sent || false}
                                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleAgentAppointmentSentChange(hire, event.target.checked)}
                                      sx={{
                                        color: theme.palette.mode === 'dark' ? '#667eea' : '#667eea',
                                        '&.Mui-checked': {
                                          color: theme.palette.mode === 'dark' ? '#667eea' : '#667eea',
                                        },
                                        '&:hover': {
                                          backgroundColor: theme.palette.mode === 'dark' 
                                            ? 'rgba(102, 126, 234, 0.1)' 
                                            : 'rgba(102, 126, 234, 0.1)',
                                        },
                                      }}
                                    />
                                  </TableCell>
                                )}
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'created_at')}</TableCell>
                                {/* Only show Updated At column for stages other than "Invitation Sent" (stage.value !== 0) */}
                                {stage.value !== 0 && (
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'update_timestamp')}</TableCell>
                                )}
                                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>{renderEditableCell(hire, 'fullName')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'state')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 200 }}>{renderEditableCell(hire, 'email')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'mobileNumber')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'instagram')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'reference')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'recruiter_numbers')}</TableCell>
                                {/* Edit Column - Moved to far right */}
                                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100, textAlign: 'center' }}>
                                  {editingId === hire.id ? (
                                    <>
                                      <Tooltip title="Save">
                                        <IconButton 
                                          onClick={handleSave} 
                                          sx={{
                                            color: '#4caf50',
                                            '&:hover': {
                                              backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                              transform: 'scale(1.1)',
                                            },
                                            transition: 'all 0.3s ease',
                                          }}
                                        >
                                          <SaveIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Cancel">
                                        <IconButton 
                                          onClick={handleCancel} 
                                          sx={{
                                            color: '#f44336',
                                            '&:hover': {
                                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                              transform: 'scale(1.1)',
                                            },
                                            transition: 'all 0.3s ease',
                                          }}
                                        >
                                          <CancelIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    <Tooltip title="Edit">
                                      <IconButton 
                                        onClick={() => handleEdit(hire)} 
                                        sx={{
                                          color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
                                          '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark' 
                                              ? 'rgba(144, 202, 249, 0.1)'
                                              : 'rgba(25, 118, 210, 0.1)',
                                            transform: 'scale(1.1)',
                                          },
                                          transition: 'all 0.3s ease',
                                        }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                })}
              </>
            )}
          </TabPanel>
        ))}
      </Paper>
      
      <ConfirmArchive
        open={confirmArchiveOpen}
        onClose={handleArchiveCancel}
        onConfirm={handleArchiveConfirm}
        hireName={hireToArchive?.fullName || ''}
      />

      {/* Notes Dialog */}
      <NotesDialog
        open={notesDialogOpen}
        onClose={() => {
          setNotesDialogOpen(false);
          setSelectedHireForNotes(null);
        }}
        hire={selectedHireForNotes}
        onSave={fetchHires}
      />

      {/* Move Hire Dialog */}
      {selectedHireForMove && (
        <MoveHireDialog
          open={moveHireOpen}
          onClose={() => {
            setMoveHireOpen(false);
            setSelectedHireForMove(null);
          }}
          hire={selectedHireForMove}
          onMoveSuccess={handleMoveSuccess}
        />
      )}
      
      {/* Actions Menu */}
      <Menu
        anchorEl={actionsMenuAnchor}
        open={Boolean(actionsMenuAnchor)}
        onClose={handleActionsMenuClose}
        PaperProps={{
          sx: {
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.3)'
              : '0 8px 32px rgba(102, 126, 234, 0.15)',
            borderRadius: 2,
            minWidth: 180,
          }
        }}
      >
        <MenuItem 
          onClick={handleEditFromMenu}
          sx={{
            color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(144, 202, 249, 0.1)' 
                : 'rgba(25, 118, 210, 0.1)',
            },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <EditIcon fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem 
          onClick={handleArchiveFromMenu}
          sx={{
            color: '#ff9800',
            '&:hover': {
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
            },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <ArchiveIcon fontSize="small" />
          Archive
        </MenuItem>
        <MenuItem 
          onClick={handleMoveFromMenu}
          sx={{
            color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(144, 202, 249, 0.1)' 
                : 'rgba(25, 118, 210, 0.1)',
            },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <TrendingUpIcon fontSize="small" />
          Move Hire
        </MenuItem>
      </Menu>
      
      <TrademarkFooter />
    </Container>
  );
};

export default CRMDashboard; 